//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type Ref } from '@hcengineering/core'

import { type Document } from './types'

/**
 * The part of a document the hierarchy walks below care about.
 *
 * Kept structural so callers can pass whatever they already hold — full
 * documents from a query, or the projections the navigator keeps in memory.
 *
 * @public
 */
export interface DocumentTreeNode {
  _id: Ref<Document>
  parent: Ref<Document>
}

/**
 * Documents grouped by their `parent`, the index every walk below expects.
 *
 * @public
 */
export function groupDocumentsByParent<T extends DocumentTreeNode> (docs: T[]): Map<Ref<Document>, T[]> {
  const byParent = new Map<Ref<Document>, T[]>()

  for (const doc of docs) {
    const group = byParent.get(doc.parent) ?? []
    group.push(doc)
    byParent.set(doc.parent, group)
  }

  return byParent
}

/**
 * Descendants of `root`, depth first.
 *
 * `stopAt` prunes whole subtrees — trashing uses it so a child that the user
 * trashed separately keeps its own Trash entry and its own retention deadline
 * instead of being absorbed into the ancestor's.
 *
 * No document is visited twice, so a `parent` cycle left behind by a partial
 * write cannot turn the walk into an infinite loop.
 *
 * @public
 */
export function collectDescendants<T extends DocumentTreeNode> (
  root: Ref<Document>,
  byParent: Map<Ref<Document>, T[]>,
  stopAt: (doc: T) => boolean = () => false
): T[] {
  const result: T[] = []
  const seen = new Set<Ref<Document>>([root])
  const queue: Array<Ref<Document>> = [root]

  while (queue.length > 0) {
    const next = queue.pop()
    if (next === undefined) break

    for (const child of byParent.get(next) ?? []) {
      if (stopAt(child) || seen.has(child._id)) continue
      seen.add(child._id)
      result.push(child)
      queue.push(child._id)
    }
  }

  return result
}

/**
 * `root` together with everything nested under it.
 *
 * This is the set a move may not land inside: re-parenting a document under one
 * of its own descendants closes a `parent` cycle, and a cycle has no root, so
 * neither the navigator nor any server-side walk would reach those documents
 * again.
 *
 * @public
 */
export function collectSubtreeRefs<T extends DocumentTreeNode> (
  root: Ref<Document>,
  byParent: Map<Ref<Document>, T[]>
): Set<Ref<Document>> {
  return new Set<Ref<Document>>([root, ...collectDescendants(root, byParent).map((it) => it._id)])
}
